"use strict";
/**
 * Optional default implementations of the AI Hub Library config helpers.
 *
 * Host applications (e.g. the deliver portal) typically inject their own
 * `apiGet/apiPost/apiPut/socketClient` so the library can reuse the host's
 * auth, base URL, CSRF, and websocket lifecycle.
 *
 * This helper exists for **standalone / demo** usage where no such host
 * infrastructure is available. It builds a minimal `fetch`-based config and
 * a no-op socket client that satisfies the `AiHubLibraryConfig` contract.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createDefaultAiHubConfig = exports.createNoopSocketClient = void 0;
var buildHeaders = function (extra, getToken, attachToken) { return __awaiter(void 0, void 0, void 0, function () {
    var headers, token;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                headers = __assign({ 'Content-Type': 'application/json', Accept: 'application/json' }, (extra || {}));
                if (!(attachToken && getToken)) return [3 /*break*/, 2];
                return [4 /*yield*/, getToken()];
            case 1:
                token = _a.sent();
                if (token) {
                    headers.Authorization = "Bearer " + token;
                }
                _a.label = 2;
            case 2: return [2 /*return*/, headers];
        }
    });
}); };
var parse = function (response) { return __awaiter(void 0, void 0, void 0, function () {
    var body, contentType;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                if (!!response.ok) return [3 /*break*/, 2];
                return [4 /*yield*/, response.text().catch(function () { return ''; })];
            case 1:
                body = _a.sent();
                throw new Error("AI Hub request failed: " + response.status + " " + response.statusText + " " + body);
            case 2:
                if (response.status === 204)
                    return [2 /*return*/, null];
                contentType = response.headers.get('content-type') || '';
                if (contentType.includes('application/json')) {
                    return [2 /*return*/, response.json()];
                }
                return [2 /*return*/, response.text()];
        }
    });
}); };
/**
 * No-op socket client. Strategy live-presence features will silently do
 * nothing. Replace with a real implementation when wiring to a backend.
 */
exports.createNoopSocketClient = function () { return ({
    strategyEditorsOn: function () {
        /* no-op */
    },
    strategyEditorsOff: function () {
        /* no-op */
    },
    strategyEditingStart: function () {
        /* no-op */
    },
    strategyEditingStop: function () {
        /* no-op */
    },
}); };
/**
 * Build a default `AiHubLibraryConfig` backed by `fetch`. Pass the result
 * straight to `configureAiHubLibrary()`.
 */
exports.createDefaultAiHubConfig = function (options) {
    if (options === void 0) { options = {}; }
    var _a = options.baseUrl, baseUrl = _a === void 0 ? '' : _a, getToken = options.getToken, fetchImpl = options.fetchImpl;
    var doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : undefined);
    if (!doFetch) {
        throw new Error('createDefaultAiHubConfig: no fetch implementation available. Pass `fetchImpl`.');
    }
    var url = function (path) { return (/^https?:/i.test(path) ? path : "" + baseUrl + path); };
    /**
     * Decide whether to attach the bearer token. We only attach when the
     * resolved request URL targets the same origin as `baseUrl` (or the
     * current document origin when `baseUrl` is empty / relative). This
     * prevents accidental token leakage when a caller passes an absolute
     * `https://attacker.example/...` path.
     */
    var documentOrigin = typeof location !== 'undefined' && location.origin ? location.origin : 'http://localhost';
    var allowedOrigin;
    try {
        allowedOrigin = baseUrl ? new URL(baseUrl, documentOrigin).origin : documentOrigin;
    }
    catch (_b) {
        allowedOrigin = documentOrigin;
    }
    var isSameOrigin = function (resolvedPath) {
        try {
            var target = new URL(resolvedPath, documentOrigin);
            return target.origin === allowedOrigin;
        }
        catch (_a) {
            return false;
        }
    };
    return {
        apiGet: function (path) { return __awaiter(void 0, void 0, void 0, function () {
            var resolvedUrl, res, _a, _b, _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        resolvedUrl = url(path);
                        _a = doFetch;
                        _b = [resolvedUrl];
                        _c = {
                            method: 'GET'
                        };
                        return [4 /*yield*/, buildHeaders(undefined, getToken, isSameOrigin(resolvedUrl))];
                    case 1: return [4 /*yield*/, _a.apply(void 0, _b.concat([(_c.headers = _d.sent(),
                                _c)]))];
                    case 2:
                        res = _d.sent();
                        return [2 /*return*/, parse(res)];
                }
            });
        }); },
        apiPost: function (path, data, headers) { return __awaiter(void 0, void 0, void 0, function () {
            var resolvedUrl, res, _a, _b, _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        resolvedUrl = url(path);
                        _a = doFetch;
                        _b = [resolvedUrl];
                        _c = {
                            method: 'POST'
                        };
                        return [4 /*yield*/, buildHeaders(headers, getToken, isSameOrigin(resolvedUrl))];
                    case 1: return [4 /*yield*/, _a.apply(void 0, _b.concat([(_c.headers = _d.sent(),
                                _c.body = JSON.stringify(data),
                                _c)]))];
                    case 2:
                        res = _d.sent();
                        return [2 /*return*/, parse(res)];
                }
            });
        }); },
        apiPut: function (path, data, headers) { return __awaiter(void 0, void 0, void 0, function () {
            var resolvedUrl, res, _a, _b, _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        resolvedUrl = url(path);
                        _a = doFetch;
                        _b = [resolvedUrl];
                        _c = {
                            method: 'PUT'
                        };
                        return [4 /*yield*/, buildHeaders(headers, getToken, isSameOrigin(resolvedUrl))];
                    case 1: return [4 /*yield*/, _a.apply(void 0, _b.concat([(_c.headers = _d.sent(),
                                _c.body = JSON.stringify(data),
                                _c)]))];
                    case 2:
                        res = _d.sent();
                        return [2 /*return*/, parse(res)];
                }
            });
        }); },
        apiPatch: function (path, data, headers) { return __awaiter(void 0, void 0, void 0, function () {
            var resolvedUrl, res, _a, _b, _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        resolvedUrl = url(path);
                        _a = doFetch;
                        _b = [resolvedUrl];
                        _c = {
                            method: 'PATCH'
                        };
                        return [4 /*yield*/, buildHeaders(headers, getToken, isSameOrigin(resolvedUrl))];
                    case 1: return [4 /*yield*/, _a.apply(void 0, _b.concat([(_c.headers = _d.sent(),
                                _c.body = JSON.stringify(data),
                                _c)]))];
                    case 2:
                        res = _d.sent();
                        return [2 /*return*/, parse(res)];
                }
            });
        }); },
        apiDelete: function (path, data, headers) { return __awaiter(void 0, void 0, void 0, function () {
            var resolvedUrl, res, _a, _b, _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        resolvedUrl = url(path);
                        _a = doFetch;
                        _b = [resolvedUrl];
                        _c = {
                            method: 'DELETE'
                        };
                        return [4 /*yield*/, buildHeaders(headers, getToken, isSameOrigin(resolvedUrl))];
                    case 1: return [4 /*yield*/, _a.apply(void 0, _b.concat([(_c.headers = _d.sent(),
                                _c.body = data === undefined ? undefined : JSON.stringify(data),
                                _c)]))];
                    case 2:
                        res = _d.sent();
                        return [2 /*return*/, parse(res)];
                }
            });
        }); },
        socketClient: exports.createNoopSocketClient(),
    };
};
